/**
 * The wholesale marketplace: buying stock from Makro, Jumbo and the rest.
 *
 * Deliberately not `billingRepository`, which is about what the shop pays
 * SmartSpaza, and not `adminRepository`, which is about every merchant on the
 * platform. This one is the shop buying, and the server resolves which shop from
 * the session — nothing here sends a merchant id to say.
 *
 * **Money arrives as decimal strings and stays that way until it is formatted.**
 * Every amount below is typed `string` for that reason: parsing a rand figure
 * into a JavaScript number to hold it in state is how a case of oil becomes
 * R219.98999999999998 on a screen a shopkeeper is deciding from.
 */

import { api } from "@/api/client";

import type { Page } from "./adminRepository";

// ═══════════════════════════════════════════════════════════════════════════════
// Shapes
// ═══════════════════════════════════════════════════════════════════════════════
export type FulfilmentMethod = "COLLECTION" | "DELIVERY";

export type OrderStatus =
  | "SCHEDULED"
  | "PLACED"
  | "SUPPLIER_CONFIRMED"
  | "PREPARING"
  | "READY_FOR_COLLECTION"
  | "COLLECTED"
  | "DISPATCHED"
  | "OUT_FOR_DELIVERY"
  | "DELIVERED"
  | "PARTIALLY_DELIVERED"
  | "CANCELLED"
  | "RETURNED"
  | "REFUND_IN_PROGRESS"
  | "REFUNDED";

export type PaymentStatus =
  "UNPAID" | "PENDING" | "PAID" | "ON_ACCOUNT" | "FAILED" | "REFUNDED";

/** What happens to a scheduled line if its group buy reaches no bulk price. */
export type PoolFallback = "BUY_ANYWAY" | "RELEASE";

export interface Supplier {
  id: string;
  name: string;
  display_name: string;
  slug: string;
  logo: string | null;
  minimum_order_value: string;
  lead_time_days: number;
  buying_days: number[];
}

export interface Listing {
  id: string;
  name: string;
  brand: string;
  image: string | null;
  pack_size: string;
  units_per_pack: string;
  barcode: string;
  /** The wholesaler's own product code, quoted at their trade counter. */
  supplier_sku: string;
  category_name: string;
  wholesaler: Supplier;
  price: string;
  /** The was-price. Null means this is not a special and the card shows no badge. */
  list_price: string | null;
  saving: string;
  is_special: boolean;
  price_per_unit: string | null;
  currency: string;
  is_zero_rated: boolean;
  vat_rate: string;
  minimum_order_quantity: number;
  order_increment: number;
  stock_on_hand: number | null;
  in_stock: boolean;
  /** Drives the BULK DEAL badge: a price that improves with volume. */
  has_bulk_price: boolean;
}

export interface QuantityTier {
  minimum_quantity: number;
  unit_price: string;
}

export interface ListingDetail extends Listing {
  description: string;
  tiers: QuantityTier[];
  /** The days this may be scheduled for, already narrowed by the cut-off. */
  schedulable_dates: string[];
}

/** Everything a group-buy card shows. Money is strings; counts are numbers. */
export interface PoolProgress {
  committed_quantity: number;
  merchant_count: number;
  target_quantity: number | null;
  current_unit_price: string;
  base_unit_price: string;
  saving_per_pack: string;
  next_tier_quantity: number | null;
  next_tier_unit_price: string | null;
  to_next_tier: number | null;
  closes_at: string;
  fulfilment_date: string;
}

export interface BuyingPool {
  id: string;
  origin: "SUPPLIER" | "SCHEDULE";
  status: "OPEN" | "CLOSED" | "CONFIRMED" | "FAILED" | "CANCELLED";
  headline: string;
  listing: Listing;
  opens_at: string;
  closes_at: string;
  fulfilment_date: string;
  target_quantity: number | null;
  base_unit_price: string;
  currency: string;
  progress: PoolProgress;
}

export interface Commitment {
  id: string;
  quantity: number;
  status: string;
  fallback: PoolFallback;
  pool: BuyingPool;
}

export interface BasketLine {
  id: string;
  listing: Listing;
  quantity: number;
  /** Null buys it now; a date pools it with other shops. */
  scheduled_for: string | null;
  pool_fallback: PoolFallback;
  indicative_total: string;
  indicative_saving: string;
}

export interface BasketSummary {
  line_count: number;
  item_count: number;
  subtotal: string;
  estimated_saving: string;
  estimated_total: string;
  scheduled_line_count: number;
  wholesaler_count: number;
}

export interface Basket {
  id: string;
  lines: BasketLine[];
  summary: BasketSummary;
  delivery_address: string;
  delivery_postal_code: string;
  delivery_instructions: string;
}

export interface OrderLine {
  id: string;
  name: string;
  image: string | null;
  pack_size: string;
  quantity: number;
  unit_price: string;
  list_price: string | null;
  saving: string;
  total: string;
  received_quantity: number;
  outstanding_quantity: number;
}

export interface MarketplaceOrder {
  id: string;
  number: string;
  wholesaler: Supplier;
  status: OrderStatus;
  payment_status: PaymentStatus;
  payment_method: string;
  fulfilment_method: FulfilmentMethod;
  expected_at: string | null;
  placed_at: string | null;
  item_count: number;
  currency: string;
  subtotal: string;
  discount_amount: string;
  delivery_fee: string;
  tax_amount: string;
  total: string;
  can_cancel: boolean;
  can_receive: boolean;
}

export interface OrderEvent {
  id: string;
  status: OrderStatus;
  note: string;
  actor_label: string;
  created_at: string;
}

export interface MarketplaceOrderDetail extends MarketplaceOrder {
  lines: OrderLine[];
  events: OrderEvent[];
  collection_point: { id: string; name: string; address_line_1: string } | null;
  delivery_address: string;
  delivery_instructions: string;
  cancellation_reason: string;
  pool: BuyingPool | null;
}

export interface CheckoutResult {
  orders: MarketplaceOrder[];
  scheduled: Commitment[];
  total: string;
}

/**
 * Filters for the product grid.
 *
 * Every field takes an explicit `undefined` as well as being optional. The
 * project runs `exactOptionalPropertyTypes`, so `{ q: search || undefined }` —
 * the natural way to write "no search term" — is a type error without it, and
 * the alternative is building the object conditionally at every call site.
 */
export interface ProductFilters {
  q?: string | undefined;
  supplier?: string | undefined;
  specials?: boolean | undefined;
  bulk?: boolean | undefined;
  /** Only the "Popular at …" listings, grouped by wholesaler in their chosen order. */
  featured?: boolean | undefined;
  page?: number | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Calls
// ═══════════════════════════════════════════════════════════════════════════════
function query(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false || value === "") continue;
    search.set(key, value === true ? "1" : String(value));
  }
  const suffix = search.toString();
  return suffix ? `?${suffix}` : "";
}

export const marketplaceRepository = {
  suppliers: () => api.get<Supplier[]>("/marketplace/suppliers/"),

  products: (filters: ProductFilters = {}) =>
    api.get<Page<Listing>>(`/marketplace/products/${query({ ...filters })}`),

  product: (id: string) =>
    api.get<ListingDetail>(`/marketplace/products/${id}/`),

  /** The group buys open for one product, with the day each closes. */
  productPools: (id: string) =>
    api.get<BuyingPool[]>(`/marketplace/products/${id}/pools/`),

  // ── "My Order" ────────────────────────────────────────────────────────────
  basket: () => api.get<Basket>("/marketplace/basket/"),

  /**
   * Add to the order. Every basket call returns the **whole panel**, because the
   * totals depend on every line and a merchant on a mobile connection should not
   * watch a second request to see one number change.
   */
  addToBasket: (input: {
    listing_id: string;
    quantity?: number | undefined;
    scheduled_for?: string | null | undefined;
    pool_fallback?: PoolFallback | undefined;
  }) => api.post<Basket>("/marketplace/basket/", input),

  setLineQuantity: (lineId: string, quantity: number) =>
    api.patch<Basket>(`/marketplace/basket/lines/${lineId}/`, { quantity }),

  removeLine: (lineId: string) =>
    api.delete<Basket>(`/marketplace/basket/lines/${lineId}/`),

  clearBasket: () => api.delete<void>("/marketplace/basket/"),

  /**
   * Place Order.
   *
   * Note what is not sent: the total. The server computed it from the basket,
   * and a client that could name what it is paying would be a client that could
   * settle a R700 order with R7.
   */
  checkout: (input: {
    fulfilment_method: FulfilmentMethod;
    collection_point_id?: string | undefined;
    delivery_address?: string | undefined;
    delivery_postal_code?: string | undefined;
    delivery_instructions?: string | undefined;
    merchant_note?: string | undefined;
  }) => api.post<CheckoutResult>("/marketplace/checkout/", input),

  // ── Group buying ──────────────────────────────────────────────────────────
  pools: (supplier?: string) =>
    api.get<Page<BuyingPool>>(`/marketplace/group-buys/${query({ supplier })}`),

  joinPool: (
    poolId: string,
    input: { quantity: number; fallback?: PoolFallback },
  ) => api.post<Commitment>(`/marketplace/group-buys/${poolId}/join/`, input),

  myPools: () => api.get<Commitment[]>("/marketplace/my-group-buys/"),

  leavePool: (commitmentId: string) =>
    api.delete<void>(`/marketplace/my-group-buys/${commitmentId}/`),

  // ── Orders ────────────────────────────────────────────────────────────────
  orders: (
    params: {
      status?: string | undefined;
      live?: boolean | undefined;
      page?: number | undefined;
    } = {},
  ) => api.get<Page<MarketplaceOrder>>(`/marketplace/orders/${query(params)}`),

  order: (id: string) =>
    api.get<MarketplaceOrderDetail>(`/marketplace/orders/${id}/`),

  cancelOrder: (id: string, reason: string) =>
    api.post<MarketplaceOrder>(`/marketplace/orders/${id}/cancel/`, { reason }),

  /** §4.3's Receive Stock. Accepted lines land in the inventory ledger. */
  receiveOrder: (
    id: string,
    input: {
      section_id: string;
      note?: string | undefined;
      lines: {
        order_line_id: string;
        accepted: number;
        rejected?: number | undefined;
        outcome?: string | undefined;
        note?: string | undefined;
      }[];
    },
  ) => api.post<{ id: string }>(`/marketplace/orders/${id}/receive/`, input),

  /**
   * Pay for an order.
   *
   * The idempotency key must be **stable across retries of one intention** —
   * minted when the dialog opens, not when the request is sent. A key per
   * request would make every retry on a slow connection a second checkout.
   */
  payOrder: (id: string, method: string, idempotencyKey: string) =>
    api.post<{ id: string; checkout_url: string }>(
      `/marketplace/orders/${id}/pay/`,
      { method },
      { idempotencyKey },
    ),
};
